import { useEffect, useRef, useState } from 'react';
import { performAsr } from '@/services/asr';

export type UseSpeechRecognizerOptions = {
  lang?: string;
  onFinalResult?: (text: string) => void;
  onError?: (err: any) => void;
  onStart?: () => void;
  onStop?: () => void;
  onAsrStart?: () => void;
  onAsrEnd?: (text: string | null) => void;
};

export function useSpeechRecognizer(options: UseSpeechRecognizerOptions) {
  const recognitionRef = useRef<any | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const mimeRef = useRef<string | null>(null);
  const countdownTimerRef = useRef<number | null>(null);
  const countdownIntervalRef = useRef<number | null>(null);
  const [recording, setRecording] = useState(false);
  const [stopIn, setStopIn] = useState<number | null>(null);

  const clearCountdowns = () => {
    if (countdownTimerRef.current) { clearTimeout(countdownTimerRef.current); countdownTimerRef.current = null; }
    if (countdownIntervalRef.current) { clearInterval(countdownIntervalRef.current); countdownIntervalRef.current = null; }
  };

  const startRecognition = async () => {
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) return false;
    try {
      const rec = new SR();
      rec.continuous = true;
      rec.interimResults = true;
      rec.lang = options.lang || 'zh-CN';
      recognitionRef.current = rec;
      rec.onresult = (e: any) => {
        let commit = '';
        for (let i = e.resultIndex; i < e.results.length; i++) {
          const res = e.results[i];
          if (res.isFinal) commit += res[0].transcript;
        }
        if (commit) options.onFinalResult?.(commit);
      };
      rec.onerror = (e: any) => {
        setRecording(false);
        setStopIn(null);
        options.onError?.(e);
        try { recognitionRef.current?.stop?.(); } catch {}
        recognitionRef.current = null;
        startFallback();
      };
      rec.start();
      setRecording(true);
      setStopIn(null);
      options.onStart?.();
      return true;
    } catch (e) {
      options.onError?.(e as any);
      return false;
    }
  };

  const startFallback = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm';
      mimeRef.current = mime;
      const rec = new MediaRecorder(stream, { mimeType: mime });
      mediaRecorderRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        const type = mimeRef.current || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type });
        const file = new File([blob], `voice_${Date.now()}.webm`, { type });
        options.onAsrStart?.();
        try {
          const text = await performAsr(file);
          options.onAsrEnd?.(text || null);
          if (text) options.onFinalResult?.(text);
        } catch (err) {
          options.onError?.(err);
          options.onAsrEnd?.(null);
        } finally {
          chunksRef.current = [];
          if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach((t) => t.stop());
          }
          mediaStreamRef.current = null;
          mediaRecorderRef.current = null;
        }
      };
      rec.start();
      setRecording(true);
      setStopIn(null);
      options.onStart?.();
    } catch (e) {
      options.onError?.(e as any);
    }
  };

  const checkSRAvailability = async (): Promise<boolean> => {
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) return false;
    try {
      const controller = new AbortController();
      const timer = window.setTimeout(() => controller.abort(), 1500);
      await fetch('https://www.gstatic.com/generate_204', { mode: 'no-cors', cache: 'no-store', signal: controller.signal });
      window.clearTimeout(timer);
      return true;
    } catch {
      return false;
    }
  };

  const start = async () => {
    await startFallback();
  };

  const requestStop = () => {
    if (!recording) return;
    clearCountdowns();
    setRecording(false);
    setStopIn(0);
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch {}
      recognitionRef.current = null;
    } else if (mediaRecorderRef.current) {
      const rec = mediaRecorderRef.current;
      try {
        if (rec.state === 'recording' || rec.state === 'paused') {
          try { rec.stop(); } catch {}
        } else {
          const type = mimeRef.current || 'audio/webm';
          const blob = new Blob(chunksRef.current, { type });
          const file = new File([blob], `voice_${Date.now()}.webm`, { type });
          options.onAsrStart?.();
          performAsr(file).then((text) => {
            options.onAsrEnd?.(text || null);
            if (text) options.onFinalResult?.(text);
          }).catch((err) => {
            options.onError?.(err);
            options.onAsrEnd?.(null);
          }).finally(() => {
            chunksRef.current = [];
            if (mediaStreamRef.current) {
              mediaStreamRef.current.getTracks().forEach((t) => t.stop());
            }
            mediaStreamRef.current = null;
            mediaRecorderRef.current = null;
          });
        }
      } catch {}
    }
    setRecording(false);
    setStopIn(null);
    options.onStop?.();
  };

  const toggle = async () => {
    if (recording) requestStop();
    else await start();
  };

  useEffect(() => {
    return () => {
      clearCountdowns();
      try { if (recognitionRef.current) recognitionRef.current.stop?.(); } catch {}
      const rec = mediaRecorderRef.current;
      if (rec && (rec.state === 'recording' || rec.state === 'paused')) { try { rec.stop(); } catch {} }
      if (mediaStreamRef.current) mediaStreamRef.current.getTracks().forEach((t) => t.stop());
      mediaRecorderRef.current = null;
      mediaStreamRef.current = null;
      recognitionRef.current = null;
    };
  }, []);

  return { recording, stopIn, toggle, requestStop };
}